import { createElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IoBookOutline,
  IoCheckmark,
  IoChevronDownOutline,
  IoMenu,
  IoPeopleOutline,
  IoPersonOutline,
  IoShareOutline,
  IoStatsChartOutline
} from 'react-icons/io5';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import { shareSite } from '../utils/shareSite';
import './Header.css';

const ShareButton = () => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/`;
    const result = await shareSite({
      url,
      share: typeof navigator.share === 'function' ? (payload) => navigator.share(payload) : null,
      writeText: navigator.clipboard?.writeText
        ? (value) => navigator.clipboard.writeText(value)
        : null
    }).catch(() => 'failed');

    if (result === 'copied') {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      type="button"
      className="header-share-button"
      onClick={handleShare}
      aria-label={copied ? 'Link copied' : 'Share ReadTheWord'}
    >
      {copied ? <IoCheckmark size={22} aria-hidden="true" /> : <IoShareOutline size={22} aria-hidden="true" />}
    </button>
  );
};

const PROFILE_LINKS = [
  { label: 'My Progress', path: '/progress', icon: IoStatsChartOutline },
  { label: 'Report reading', path: '/report', icon: IoBookOutline },
  { label: 'Family', path: '/family', icon: IoPeopleOutline },
  { label: 'Profile', path: '/settings', icon: IoPersonOutline }
];

const Header = ({ onMenuClick }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const menuRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Check if we're on the auth page
  const isOnAuthPage = location.pathname === '/auth';

  // The menu renders into <body>: the header clips overflow, so a menu inside
  // it is cut off at the header's edge. Pin it under the profile control.
  useLayoutEffect(() => {
    if (!profileOpen) return undefined;
    const place = () => {
      const rect = profileRef.current?.getBoundingClientRect();
      if (rect) {
        setMenuAnchor({
          top: rect.bottom + 8,
          right: Math.max(8, window.innerWidth - rect.right)
        });
      }
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [profileOpen]);

  useEffect(() => {
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!profileOpen) return undefined;
    const handlePointerDown = (event) => {
      const inProfile = profileRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inProfile && !inMenu) setProfileOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setProfileOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [profileOpen]);

  return (
    <>
      {/* Mobile Church Display */}
      {isAuthenticated && user?.church && (
        <div className="mobile-church-display">
          <span className="mobile-church-name">{user.church.name}</span>
        </div>
      )}
      <header className="header">
        <div className="header-content">
        <div className="header-left">
          {isAuthenticated && (
            <button 
              className="menu-button"
              onClick={onMenuClick}
              aria-label="Open menu"
            >
              <IoMenu size={24} />
            </button>
          )}
          <Link to="/" className="title-link">
            <div className="title-container">
              <img src="/logo.jpeg" alt="ReadTheWord Logo" className="app-logo" />
              <h1 className="app-title">ReadTheWord</h1>
            </div>
          </Link>
          {isAuthenticated && user?.church && (
            <div className="church-display">
              <span className="church-name">{user.church.name}</span>
            </div>
          )}
        </div>
        
        <div className="header-right">
          <ShareButton />
          {isAuthenticated ? (
            <>
              {/* User Profile Section */}
              <div className="user-profile-section" ref={profileRef}>
                <button
                  type="button"
                  className="user-profile-link"
                  onClick={() => setProfileOpen((open) => !open)}
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                  aria-label="Account menu"
                >
                  <div className="profile-picture-container">
                    <div className="profile-picture">
                      <span className="profile-initial">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  </div>
                  <span className="user-name">
                    {user?.name || 'User'}
                  </span>
                  <IoChevronDownOutline
                    className={`profile-menu-chevron${profileOpen ? ' open' : ''}`}
                    size={16}
                    aria-hidden="true"
                  />
                </button>
                {profileOpen && createPortal(
                  <div
                    className="profile-menu"
                    role="menu"
                    ref={menuRef}
                    style={menuAnchor ? {
                      '--profile-menu-top': `${menuAnchor.top}px`,
                      '--profile-menu-right': `${menuAnchor.right}px`
                    } : undefined}
                  >
                    {PROFILE_LINKS.map(({ label, path, icon }) => (
                      <Link
                        key={path}
                        to={path}
                        role="menuitem"
                        className={`profile-menu-item${location.pathname === path ? ' active' : ''}`}
                        onClick={() => setProfileOpen(false)}
                      >
                        {createElement(icon, { size: 18, 'aria-hidden': true })}
                        <span>{label}</span>
                      </Link>
                    ))}
                  </div>,
                  document.body
                )}
              </div>

              {/* Notification Bell */}
              <NotificationBell />
            </>
          ) : !isOnAuthPage ? (
            <div className="auth-buttons">
              <Link to="/auth" className="login-button">
                Sign in
              </Link>
              <Link to="/auth?view=register" className="signup-button">
                Sign up
              </Link>
            </div>
          ) : null}
        </div>
      </div>
      </header>
    </>
  );
};

export default Header;
